import { DIRECTION_VECTORS, type Direction } from './direction';

export interface CellPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * One arrow: a self-avoiding path of orthogonally connected cells, ending in a head.
 *
 * `cells` runs from tail to head. `direction` is where the head points, which for any arrow
 * longer than one cell is exactly the direction of the final step, so the drawn arrowhead
 * and the movement rule can never disagree.
 *
 * A length-1 arrow is simply the degenerate case, which keeps the tutorial levels and the
 * rules identical for short and long arrows alike.
 */
export interface ArrowData {
  readonly id: number;
  readonly cells: readonly CellPoint[];
  readonly direction: Direction;
}

export type ArrowShape = Omit<ArrowData, 'id'>;

export function arrowHead(arrow: ArrowData | ArrowShape): CellPoint {
  const head = arrow.cells[arrow.cells.length - 1];
  if (head === undefined) throw new Error('Arrow has no cells');
  return head;
}

export function arrowTail(arrow: ArrowData | ArrowShape): CellPoint {
  const tail = arrow.cells[0];
  if (tail === undefined) throw new Error('Arrow has no cells');
  return tail;
}

export function arrowLength(arrow: ArrowData | ArrowShape): number {
  return arrow.cells.length;
}

/** True when the path is orthogonally connected, self-avoiding and ends along `direction`. */
export function isWellFormedPath(arrow: ArrowShape): boolean {
  const { cells, direction } = arrow;
  if (cells.length === 0) return false;

  const seen = new Set<string>();
  for (let i = 0; i < cells.length; i += 1) {
    const cell = cells[i];
    if (!Number.isInteger(cell.x) || !Number.isInteger(cell.y)) return false;
    const key = `${cell.x},${cell.y}`;
    if (seen.has(key)) return false;
    seen.add(key);

    if (i > 0) {
      const previous = cells[i - 1];
      const dx = Math.abs(cell.x - previous.x);
      const dy = Math.abs(cell.y - previous.y);
      if (dx + dy !== 1) return false;
    }
  }

  if (cells.length >= 2) {
    const head = cells[cells.length - 1];
    const beforeHead = cells[cells.length - 2];
    const step = DIRECTION_VECTORS[direction];
    if (head.x - beforeHead.x !== step.dx || head.y - beforeHead.y !== step.dy) return false;
  }
  return true;
}

/**
 * Row-major comparison of head cells. Heads are unique because arrow paths never overlap,
 * so this is a total order and gives every board one canonical arrow numbering.
 */
export function compareArrowsRowMajor(a: ArrowShape, b: ArrowShape): number {
  const headA = arrowHead(a);
  const headB = arrowHead(b);
  if (headA.y !== headB.y) return headA.y - headB.y;
  return headA.x - headB.x;
}

/** Builds a path from a start cell and a string of steps such as `"DDRR"`. */
export function pathFromSteps(
  startX: number,
  startY: number,
  steps: string,
  fallbackDirection: Direction = 'up',
): ArrowShape {
  const cells: CellPoint[] = [{ x: startX, y: startY }];
  let direction = fallbackDirection;

  for (const step of steps) {
    const next = STEP_DIRECTIONS[step.toUpperCase()];
    if (next === undefined) throw new Error(`Unknown path step: ${step}`);
    direction = next;
    const previous = cells[cells.length - 1];
    const vector = DIRECTION_VECTORS[next];
    cells.push({ x: previous.x + vector.dx, y: previous.y + vector.dy });
  }
  return { cells, direction };
}

const STEP_DIRECTIONS: Record<string, Direction> = {
  U: 'up',
  R: 'right',
  D: 'down',
  L: 'left',
};
